import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  input,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Editor, NgxEditorModule, Toolbar } from 'ngx-editor';
import { startWith } from 'rxjs';

import {
  Content,
  LargeModalContent,
  UpdateContentBlockPayload,
} from '@app/utils/interfaces/content.interface';
import { EditorBodyAdapter } from '@app/utils/interfaces/editor-interface';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-edit-modal',
  standalone: true,
  imports: [ReactiveFormsModule, NgxEditorModule, TranslateModule],
  templateUrl: './edit-modal.component.html',
  styleUrl: './edit-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditModalComponent
  implements OnInit, OnDestroy, EditorBodyAdapter<UpdateContentBlockPayload>
{
  public readonly data = input.required<Content>();
  public readonly isPreviewMode = input(false);

  protected editor!: Editor;
  protected readonly maxLength = 50000;

  protected readonly toolbar: Toolbar = [
    ['bold', 'italic', 'underline', 'strike'],
    ['ordered_list', 'bullet_list'],
    ['align_left', 'align_center', 'align_right', 'align_justify'],
    ['link'],
    ['text_color', 'background_color'],
  ];

  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly form = this.fb.nonNullable.group({
    isVisible: [true],
    content: ['', [Validators.required, Validators.maxLength(this.maxLength)]],
  });

  private readonly contentControlValue = toSignal(
    this.form.controls.content.valueChanges.pipe(startWith(this.form.controls.content.value)),
    { initialValue: this.form.controls.content.value },
  );

  protected readonly contentValue = computed(() => this.contentControlValue() ?? '');

  protected readonly contentLength = computed(() => {
    const div = document.createElement('div');
    div.innerHTML = this.contentValue();

    return (div.textContent || div.innerText || '').length;
  });

  public ngOnInit(): void {
    this.editor = new Editor();

    this.form.patchValue({
      isVisible: this.data().isVisible,
      content: this.data().content ?? '',
    });

    this.form.controls.content.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value: string) => {
        if ((value?.length ?? 0) > this.maxLength) {
          this.form.controls.content.setValue(value.slice(0, this.maxLength), {
            emitEvent: false,
          });
        }
      });
  }

  public ngOnDestroy(): void {
    this.editor?.destroy();
  }

  public isValid(): boolean {
    return this.form.valid;
  }

  public markAllAsTouched(): void {
    this.form.markAllAsTouched();
  }

  public hasChanges(): boolean {
    const formValue = this.form.getRawValue();
    const currentData = this.data();

    return (
      formValue.isVisible !== currentData.isVisible ||
      formValue.content !== (currentData.content ?? '')
    );
  }

  public canPreview(): boolean {
    return true;
  }

  public getPreviewContent(): LargeModalContent {
    return {
      titleKey: `shop-settings.content.table.section-type.${this.data().sectionType}`,
      text: this.contentValue(),
    };
  }

  public getVisibilityValue(): boolean {
    return this.form.controls.isVisible.value;
  }

  public setVisibilityValue(value: boolean): void {
    this.form.controls.isVisible.setValue(value);
  }

  public getSubmitPayload(): UpdateContentBlockPayload {
    const formValue = this.form.getRawValue();

    return {
      isVisible: formValue.isVisible,
      content: formValue.content,
    };
  }
}
